import { Signal } from '@angular/core';

import { TranslateTokensFn, translateSignal } from './translate-signal.util';
import { TranslationTokens } from './translate.util';

/** The minimal shape an item must have to be translated — the rest of it is passed through untouched. */
type Labelled<K extends string> = { readonly [P in K]: TranslationTokens; } & { readonly children?: Labelled<K>[] | number; };

export type Translated<T, K extends string = 'label'> = {
  [P in keyof T]: P extends K ? string : P extends 'children' ? TranslatedChildren<T[P], K> : T[P];
};

type TranslatedChildren<T, K extends string> = T extends (infer U)[] ? Translated<U, K>[] : T;

export type Untranslated<T extends { readonly label: string; }> = {
  [P in keyof T]: P extends 'label' ? TranslationTokens : P extends 'children' ? UntranslatedChildren<T[P]> : T[P];
};

type UntranslatedChildren<T> = T extends { readonly label: string; }[] ? Untranslated<T[number]>[] : T;

/**
 * Resolves the `labelKey` property of every item, and of every item nested under `children`, to a display-ready
 * string, re-emitting on language change. Pass a function source (a signal, or an arrow reading signals) so the items
 * also re-derive when the data changes. A numeric `children` is a lazy-load count, so it passes through as is.
 * Must be called in an injection context
 */
export function translateLabels<T extends Labelled<K>, K extends string = 'label'>(
  source: T[] | (() => T[]),
  labelKey: K = 'label' as K,
): Signal<Translated<T, K>[]> {
  return translateSignal((translateTokens) => {
    const items = typeof source === 'function' ? source() : source;

    return resolveLabels(items, labelKey, translateTokens);
  });
}

function resolveLabels<T extends Labelled<K>, K extends string>(
  items: T[],
  labelKey: K,
  translateTokens: TranslateTokensFn,
): Translated<T, K>[] {
  return items.map((item) => ({
    ...item,
    [labelKey]: translateTokens(item[labelKey]),
    ...Array.isArray(item.children) && { children: resolveLabels(item.children, labelKey, translateTokens) },
  }) as Translated<T, K>);
}
